import React, { useState } from 'react';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import { useAutocomplete } from '@mui/material';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';

import { BLACK, SCOUT_TEAL, WHITE } from '../../../ThemeContext/constants';

import type { AutocompleteGetTagProps } from '@mui/material';
import type { AutocompleteInputChangeReason } from '@mui/material/Autocomplete';
import type { UIEventHandler } from 'react';

export interface IOptionType {
  name: string;
  value?: string;
  id?: number;
  state?: string;
  label?: string;
}

export interface IMultiSelectProps<TOption extends IOptionType = IOptionType> {
  name: string;
  selectOptions: TOption[];
  handleFilterChange: (filter: string, newValues: (string | TOption)[]) => void;
  filters: TOption[];
  freeInput?: boolean;
  disabled?: boolean;
  displayLabel?: boolean;
  onInputChange?: (value: string, reason: AutocompleteInputChangeReason) => void;
  onListboxScroll?: UIEventHandler<HTMLUListElement>;
}

const InputWrapper = styled('div')(
  () => `
  width: 100%;
  min-height: 41px;
  border: 1px solid #DADEE0;
  background-color: ${WHITE};
  border-radius: 6px;
  padding: 6px 22px 6px 6px;
  display: flex;
  flex-wrap: wrap;
  margin-top: 3px;
  &:hover {
    border-color: #DADEE0;
  }

  &.focused {
    border-color: #77C2BE;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }

  & input {
    background-color: transparent;
    color: ${BLACK};
    box-sizing: border-box;
    padding: 4px 6px 2px 6px;
    width: 0;
    height: 26px;
    flex-grow: 1;
    border: 0;
    margin: 0;
    outline: 0;
    z-index: 1;
  }

  & .select-placeholder {
    font-size: 12px;
    font-weight: 500;
    color: ${BLACK};
    margin-left: 8px;
    margin-top: 2px;
    position: absolute;
    z-index: 0;
  }

  & .select-arrow {
    position: absolute;
    right: 4px;
    bottom: 0px;
    height: 100%;
    fontSize: 1.1rem;
    color: #AAAAAA;
    transition: transform 0.25s ease-in-out;
  }
`
);

interface TagProps extends ReturnType<AutocompleteGetTagProps> {
  label: string;
}

const Tag = (props: TagProps) => {
  const { label, onDelete, ...other } = props;
  return (
    <div {...other}>
      <span>{label}</span>
      <CancelIcon onClick={onDelete} />
    </div>
  );
};

const StyledTag = styled(Tag)<TagProps>(
  () => `
  display: flex;
  align-items: center;
  font-size: 10px;
  font-weight: 500;
  color: ${BLACK};
  height: 23px;
  margin: 2px;
  line-height: 22px;
  background-color: #F3F3F3;
  border: none;
  border-radius: 0px;
  box-sizing: content-box;
  padding: 0 4px 0 10px;
  outline: 0;
  overflow: hidden;

  & span {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  & svg {
    font-size: 9px;
    cursor: pointer;
    margin: 0px 4px 0px 6px;
  }
`
);

const Listbox = styled('ul')(
  () => `
  width: 220px;
  margin: 4px 0 0;
  padding: 0;
  list-style: none;
  background-color: ${WHITE};
  overflow: auto;
  overflow-x: hidden;
  max-height: 160px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  & .Mui-focused {
    background: #F1F1F1 !important;
  }

  & li {
    padding: 5px 12px;
    display: flex;
    font-weight: 600;
    user-select: none;
    -moz-user-select: none;
    -khtml-user-select: none;
    -webkit-user-select: none;
    -o-user-select: none;
    cursor: pointer;

    & span {
      flex-grow: 1;
    }

    & svg {
      color: transparent;
    }

  }

  & li[aria-selected='true'] {
    background-color: #FAFAFA;
    font-weight: 600;

    & svg {
      color: ${SCOUT_TEAL};
    }
  }

  & li[data-focus='true'] {
    background-color: #e6f7ff;
    cursor: pointer;  

    & svg {
      color: currentColor;
    }
  }
`
);

const MultiSelect = <TOption extends IOptionType = IOptionType>({
  name,
  selectOptions,
  handleFilterChange,
  filters,
  freeInput = false,
  disabled = false,
  displayLabel = false,
  onInputChange,
  onListboxScroll
}: IMultiSelectProps<TOption>) => {
  const [open, setOpen] = useState(false);
  const {
    getRootProps,
    getInputProps,
    getTagProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    value,
    focused,
    anchorEl,
    setAnchorEl
  } = useAutocomplete({
    open,
    onOpen: () => setOpen(true),
    onClose: () => setOpen(false),
    id: 'multi-select',
    multiple: true,
    options: selectOptions ?? [],
    getOptionLabel: (option: string | TOption) =>
      typeof option === 'string' ? option : option.name,
    value: filters,
    onChange: (_event, selectedValues) => handleFilterChange(name, selectedValues),
    onInputChange: (_event, inputValue, reason) => onInputChange?.(inputValue, reason),
    isOptionEqualToValue: (option, selectedValue) => {
      if (typeof option === 'string' || typeof selectedValue === 'string') {
        return option === selectedValue;
      }
      if (option.id != null && selectedValue.id != null) return option.id === selectedValue.id;
      if (option.value != null && selectedValue.value != null) {
        return option.value === selectedValue.value;
      }
      return option.name === selectedValue.name;
    },
    freeSolo: freeInput,
    autoSelect: freeInput,
    disabled: disabled && filters.length === 0
  });
  const displayOption = (option: string | TOption) => {
    if (typeof option === 'string') {
      return option;
    }
    return displayLabel ? (option.label ?? option.name) : option.name;
  };
  return (
    <>
      <div {...getRootProps()}>
        <InputWrapper
          ref={setAnchorEl}
          className={focused ? 'focused' : ''}
          sx={{ position: 'relative', ...(disabled && { opacity: 0.5 }) }}
        >
          {value.length < 1 && !focused && !disabled && (
            <span className="select-placeholder">
              {freeInput ? 'Input one or more' : 'Please select'}
            </span>
          )}
          {!freeInput && (
            <ArrowDropDownIcon
              className="select-arrow"
              onClick={() => setOpen(!open)}
              sx={
                open
                  ? { transform: 'rotate(-180deg)', '&:hover': { cursor: 'pointer' } }
                  : { '&:hover': { cursor: 'pointer' } }
              }
            />
          )}
          {value.map((option: string | TOption, index: number) => (
            <StyledTag
              {...getTagProps({ index })}
              key={typeof option === 'string' ? option : (option.id ?? option.value ?? option.name)}
              label={displayOption(option)}
            />
          ))}
          <input {...getInputProps()} />
        </InputWrapper>
      </div>
      {groupedOptions.length > 0 ? (
        <Popper
          open
          anchorEl={anchorEl}
          placement="bottom-start"
          sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        >
          <Listbox {...getListboxProps()} onScroll={onListboxScroll}>
            {(groupedOptions as TOption[]).map((option, index) => (
              <li
                {...getOptionProps({ option, index })}
                key={option.id ?? option.value ?? option.name}
                data-testid={`option-${index}`}
              >
                <span>{displayLabel ? option.label : option.name}</span>
                <CheckIcon fontSize="small" />
              </li>
            ))}
          </Listbox>
        </Popper>
      ) : null}
    </>
  );
};

export default MultiSelect;
